import { readMonitoringRotationIntervalSec } from './monitoringRotationInterval';

/** 팝업 주소창에 보이는 짧은 경로 (예: /POOL002/grid) */
export function buildCctvPoolGridPopupPath(
  poolCode: string,
  options?: {
    /** 시설 순환 목록 (코드, 표시 순) */
    poolCodes?: string[] | null;
    intervalSec?: number;
    startPaused?: boolean;
    /** true면 새 창이 아니라 현재 창에서 표시 (닫기=뒤로가기) */
    inplace?: boolean;
    /** inplace 시 닫기 후 복귀 경로 (예: /central-monitoring) */
    returnTo?: string | null;
  }
): string {
  const params = new URLSearchParams();
  const codes = (options?.poolCodes || [])
    .map((c) => String(c || '').trim())
    .filter(Boolean);
  if (codes.length > 0) {
    params.set('poolCodes', Array.from(new Set(codes)).join(','));
  }
  if (options?.intervalSec != null && Number.isFinite(options.intervalSec)) {
    params.set(
      'interval',
      String(Math.min(300, Math.max(1, Math.floor(options.intervalSec))))
    );
  }
  if (options?.startPaused) {
    params.set('paused', '1');
  }
  if (options?.inplace) {
    params.set('inplace', '1');
  }
  const returnTo = options?.returnTo != null ? String(options.returnTo).trim() : '';
  if (returnTo && returnTo.startsWith('/')) {
    params.set('returnTo', returnTo);
  }
  const qs = params.toString();
  return `/${encodeURIComponent(poolCode)}/grid${qs ? `?${qs}` : ''}`;
}

export type CctvPoolRotationPopupPathOptions = {
  intervalSec?: number;
  /** 순환 시작 시 표시할 CCTV */
  cctvId?: number;
  /** true면 순환 일시정지 상태로 진입 */
  startPaused?: boolean;
  /** true면 새 창이 아니라 현재 창에서 표시 */
  inplace?: boolean;
  /** inplace 시 닫기 후 복귀 경로 */
  returnTo?: string | null;
};

export function buildCctvPoolRotationPopupPath(
  poolCode: string,
  intervalSecOrOptions?: number | CctvPoolRotationPopupPathOptions
): string {
  let intervalSec: number | undefined;
  let cctvId: number | undefined;
  let startPaused: boolean | undefined;
  let inplace: boolean | undefined;
  let returnTo: string | null | undefined;
  if (typeof intervalSecOrOptions === 'number') {
    intervalSec = intervalSecOrOptions;
  } else if (intervalSecOrOptions) {
    intervalSec = intervalSecOrOptions.intervalSec;
    cctvId = intervalSecOrOptions.cctvId;
    startPaused = intervalSecOrOptions.startPaused;
    inplace = intervalSecOrOptions.inplace;
    returnTo = intervalSecOrOptions.returnTo;
  }
  const sec = Math.min(
    300,
    Math.max(1, Math.floor(intervalSec ?? readMonitoringRotationIntervalSec()) || 5)
  );
  const params = new URLSearchParams();
  params.set('interval', String(sec));
  if (cctvId != null && Number.isFinite(Number(cctvId))) {
    params.set('cctvId', String(Math.floor(Number(cctvId))));
  }
  if (startPaused) {
    params.set('paused', '1');
  }
  if (inplace) {
    params.set('inplace', '1');
  }
  const ret = returnTo != null ? String(returnTo).trim() : '';
  if (ret && ret.startsWith('/')) {
    params.set('returnTo', ret);
  }
  return `/${encodeURIComponent(poolCode)}/rotation?${params.toString()}`;
}

/** 전체 시설 그리드 (예: /all/cctv) */
export function buildCctvCentralAllPopupPath(options?: {
  poolsPerRow?: number;
  rotationIntervalSec?: number;
  adminId?: number | null;
  /** 이미 필터된 시설만 표시 (개별 관제용) */
  poolIds?: Array<number | string> | null;
}): string {
  const params = new URLSearchParams();
  if (options?.poolsPerRow != null && Number.isFinite(options.poolsPerRow)) {
    params.set('cols', String(Math.max(1, Math.floor(options.poolsPerRow))));
  }
  if (options?.rotationIntervalSec != null && Number.isFinite(options.rotationIntervalSec)) {
    params.set(
      'interval',
      String(
        Math.min(300, Math.max(1, Math.floor(options.rotationIntervalSec)))
      )
    );
  }
  const adminId = options?.adminId != null ? Number(options.adminId) : NaN;
  if (Number.isFinite(adminId) && adminId > 0) {
    params.set('adminId', String(Math.floor(adminId)));
  }
  if (Array.isArray(options?.poolIds) && options.poolIds.length > 0) {
    const ids = options.poolIds
      .map((id) => Math.floor(Number(id)))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length > 0) {
      params.set('poolIds', Array.from(new Set(ids)).join(','));
    }
  }
  const qs = params.toString();
  return `/all/cctv${qs ? `?${qs}` : ''}`;
}

export function buildCctvPopupAbsoluteUrl(path: string): string {
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
}

export const CENTRAL_MONITORING_WINDOW_NAME = 'pool_central_monitoring';

/** 통합 관제 모니터링 — 새 탭이 아닌 별도 팝업 창 (opener 유지로 접근 키 세션 상속) */
export function openCentralMonitoringPopupWindow(): Window | null {
  if (typeof window === 'undefined') return null;
  const url = buildCctvPopupAbsoluteUrl('/central-monitoring');
  const width = Math.min(window.screen.availWidth, 1920);
  const height = Math.min(window.screen.availHeight, 1080);
  const left = Math.round(window.screenX + Math.max(0, (window.outerWidth - width) / 2));
  const top = Math.round(window.screenY + Math.max(0, (window.outerHeight - height) / 2));
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'menubar=no',
    'toolbar=no',
    'location=yes',
    'status=no',
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');
  const win = window.open(url, CENTRAL_MONITORING_WINDOW_NAME, features);
  if (!win) {
    window.alert('팝업 창을 여기 불가. 브라우저에서 팝업 허용을 확인.');
    return null;
  }
  try {
    win.focus();
  } catch {
    // ignore
  }
  return win;
}

const POOL_MANAGEMENT_WINDOW_PREFIX = 'pool_management_';

/** 시설 관리 화면 — 별도 팝업 창 (예: /pool-management/POOL001) */
export function openPoolManagementPopupWindow(poolCode: string): Window | null {
  if (typeof window === 'undefined') return null;
  const code = String(poolCode || '').trim();
  if (!code) return null;
  const url = buildCctvPopupAbsoluteUrl(`/pool-management/${encodeURIComponent(code)}`);
  const width = Math.min(window.screen.availWidth, 1600);
  const height = Math.min(window.screen.availHeight, 960);
  const left = Math.round(window.screenX + Math.max(0, (window.outerWidth - width) / 2));
  const top = Math.round(window.screenY + Math.max(0, (window.outerHeight - height) / 2));
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'menubar=no',
    'toolbar=no',
    'location=yes',
    'status=no',
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');
  const win = window.open(url, `${POOL_MANAGEMENT_WINDOW_PREFIX}${code}`, features);
  if (!win) {
    window.alert('팝업 창을 여기 불가. 브라우저에서 팝업 허용을 확인.');
    return null;
  }
  try {
    win.focus();
  } catch {
    // ignore
  }
  return win;
}

export const INDIVIDUAL_MONITORING_WINDOW_NAME = 'pool_individual_monitoring';

export type CommunicationPanelKey = 'messages' | 'facility-board' | 'control-archive';

const COMMUNICATION_WINDOW_PREFIX = 'pool_communication_';

/** 개별 관제 툴바 — 관제 메시지 / 시설 게시판 / 관제 자료실 팝업 */
export function openCommunicationPopupWindow(
  panel: CommunicationPanelKey
): Window | null {
  if (typeof window === 'undefined') return null;
  const key: CommunicationPanelKey =
    panel === 'facility-board' || panel === 'control-archive' ? panel : 'messages';
  const url = buildCctvPopupAbsoluteUrl(`/communication?panel=${encodeURIComponent(key)}`);
  const width = Math.min(960, Math.max(720, Math.floor(window.screen.availWidth * 0.55)));
  const height = Math.min(900, Math.max(640, Math.floor(window.screen.availHeight * 0.8)));
  const left = Math.round(window.screenX + Math.max(0, (window.outerWidth - width) / 2));
  const top = Math.round(window.screenY + Math.max(0, (window.outerHeight - height) / 2));
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'menubar=no',
    'toolbar=no',
    'location=yes',
    'status=no',
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');
  const win = window.open(url, `${COMMUNICATION_WINDOW_PREFIX}${key}`, features);
  if (!win) {
    window.alert('팝업 창을 여기 불가. 브라우저에서 팝업 허용을 확인.');
    return null;
  }
  try {
    win.focus();
  } catch {
    // ignore
  }
  return win;
}

/** 개별 관제(최고관리자): 선택한 일반관리자 배당 시설만 팝업 */
export function openIndividualMonitoringPopupWindow(adminId: number): Window | null {
  if (typeof window === 'undefined') return null;
  const id = Math.floor(Number(adminId));
  if (!Number.isFinite(id) || id <= 0) return null;
  const url = buildCctvPopupAbsoluteUrl(`/individual-monitoring?adminId=${id}`);
  const width = Math.min(window.screen.availWidth, 1920);
  const height = Math.min(window.screen.availHeight, 1080);
  const left = Math.round(window.screenX + Math.max(0, (window.outerWidth - width) / 2));
  const top = Math.round(window.screenY + Math.max(0, (window.outerHeight - height) / 2));
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'menubar=no',
    'toolbar=no',
    'location=yes',
    'status=no',
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');
  const win = window.open(url, `${INDIVIDUAL_MONITORING_WINDOW_NAME}_${id}`, features);
  if (!win) {
    window.alert('팝업 창을 여기 불가. 브라우저에서 팝업 허용을 확인.');
    return null;
  }
  try {
    win.focus();
  } catch {
    // ignore
  }
  return win;
}

/** 짧은 CCTV 팝업 경로 여부 (/:poolCode/grid|rotation) */
export const CCTV_POPUP_SHORT_PATH_RE = /^\/([^/]+)\/(grid|rotation)$/;

/** 이 첫 경로 세그먼트는 시설 코드로 쓰이지 않음 */
export const CCTV_POPUP_RESERVED_SEGMENTS = new Set([
  'login',
  'register',
  'reset-password',
  'verify-admin',
  'dashboard',
  'central-control',
  'main-monitoring',
  'central-monitoring',
  'physiological-monitoring',
  'pool-management',
  'pool-monitoring',
  'pool',
  'pools',
  'settings',
  'contact-management',
  'cctv-fullscreen',
  'cctv-popup',
  'all',
  'monitoring',
  'individual-monitoring',
  'communication',
  'operator-messages',
  'users',
]);

export function isShortPoolCctvPopupPath(pathname: string): boolean {
  const m = pathname.match(CCTV_POPUP_SHORT_PATH_RE);
  if (!m) return false;
  const seg = decodeURIComponent(m[1]);
  return !CCTV_POPUP_RESERVED_SEGMENTS.has(seg.toLowerCase());
}

/** 시설 관리 단축 진입 (예: /POOL001 → 관리 화면, 주소창 유지) */
const SHORT_POOL_ENTRY_RE = /^\/([^/]+)\/?$/;

export function isShortPoolEntryPath(pathname: string): boolean {
  const m = pathname.match(SHORT_POOL_ENTRY_RE);
  if (!m) return false;
  const seg = decodeURIComponent(m[1]).toLowerCase();
  return !CCTV_POPUP_RESERVED_SEGMENTS.has(seg);
}

/** 시설 모니터링 단축 경로 (예: /POOL001/monitoring) */
const SHORT_POOL_MONITORING_RE = /^\/([^/]+)\/monitoring\/?$/;

export function isShortPoolMonitoringPath(pathname: string): boolean {
  const m = pathname.match(SHORT_POOL_MONITORING_RE);
  if (!m) return false;
  const seg = decodeURIComponent(m[1]).toLowerCase();
  return !CCTV_POPUP_RESERVED_SEGMENTS.has(seg);
}

/**
 * 시설 URL에서 시설 코드 추출 (/CODE, /CODE/grid, /pool-management/CODE 등).
 * WebSocketProvider처럼 Route params 밖에서도 사용.
 */
export function extractPoolCodeFromPathname(pathname: string): string | null {
  const path = String(pathname || '').split('?')[0] || '';
  const mMgmt = path.match(/^\/pool-management\/([^/]+)/i);
  if (mMgmt) return decodeURIComponent(mMgmt[1]);
  const mMon = path.match(/^\/pool-monitoring\/([^/]+)/i);
  if (mMon) return decodeURIComponent(mMon[1]);
  const mPool = path.match(/^\/pool\/([^/]+)/i);
  if (mPool) {
    const seg = decodeURIComponent(mPool[1]);
    if (seg.toLowerCase() === 'id') return null;
    return seg;
  }
  const mShort = path.match(CCTV_POPUP_SHORT_PATH_RE);
  if (mShort) {
    const seg = decodeURIComponent(mShort[1]);
    if (!CCTV_POPUP_RESERVED_SEGMENTS.has(seg.toLowerCase())) return seg;
  }
  if (isShortPoolEntryPath(path) || isShortPoolMonitoringPath(path)) {
    const seg = decodeURIComponent(path.split('/').filter(Boolean)[0] || '');
    return seg || null;
  }
  return null;
}

/**
 * 현장·개별 시설 URL — 앱 접근 비밀키 없이 시설 비밀번호(PoolAccessGuard)만 사용
 */
export function isIndividualPoolSitePath(pathname: string): boolean {
  if (/^\/pool-management\/[^/]+/.test(pathname)) return true;
  if (/^\/pool-monitoring\/[^/]+/.test(pathname)) return true;
  if (/^\/pool\/id\/[^/]+/.test(pathname)) return true;
  if (/^\/pool\/[^/]+/.test(pathname)) return true;
  if (isShortPoolEntryPath(pathname)) return true;
  if (isShortPoolMonitoringPath(pathname)) return true;
  if (isShortPoolCctvPopupPath(pathname)) return true;
  return false;
}

export function shortPathLabel(pathname: string, search = ''): string {
  const m = pathname.match(CCTV_POPUP_SHORT_PATH_RE);
  if (!m) return pathname + search;
  const code = decodeURIComponent(m[1]);
  const mode = m[2];
  return `${code}/${mode}${search}`;
}
